import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, interval } from 'rxjs';
import { switchMap, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

// ============================================================================
// INTERFACES Y TIPOS DEL SERVICIO DE POLLING DE PAGOS
// ============================================================================

/**
 * Tipo que representa los posibles estados de procesamiento de un pago.
 * - 'pending': El pago está en cola esperando ser procesado
 * - 'processing': El pago se está procesando activamente
 * - 'completed': El pago se procesó exitosamente
 * - 'completed_with_errors': El pago se procesó pero con errores en operaciones secundarias
 * - 'failed': El pago falló durante el procesamiento
 */
export type EstadoProcesamiento = 'pending' | 'processing' | 'completed' | 'completed_with_errors' | 'failed';

/**
 * Interfaz que representa el estado completo de un trabajo de pago
 * en el frontend. Contiene toda la información necesaria para mostrar
 * el progreso y estado del pago en el panel de monitoreo.
 */
export interface TrabajoPago {
  /** ID del pago en el backend */
  id: number;

  /** Número de pago para mostrar al usuario */
  numeroPago: string;

  /** Nombre del proveedor o empresa destino del pago */
  nombreDestino: string;

  /** Total de facturas incluidas en el pago */
  totalFacturas: number;

  /** Cantidad de facturas procesadas hasta el momento */
  facturasProcessadas: number;

  /** Porcentaje de progreso del procesamiento (0-100) */
  porcentajeProgreso: number;

  /** Estado actual del procesamiento del pago */
  estado: EstadoProcesamiento;

  /** Mensaje de error retornado por el backend (si aplica, truncado a 200 chars) */
  mensajeError: string | null;

  /** Timestamp ISO de cuando se registró el trabajo en el frontend */
  iniciadoEn: string;

  /** Timestamp ISO de cuando el pago se completó exitosamente */
  completadoEn: string | null;

  /** Timestamp ISO de cuando el pago falló */
  fallidoEn: string | null;

  /** Tiempo total de procesamiento en segundos (reportado por el backend) */
  tiempoProcesamiento: number | null;

  /** Contador de reintentos consecutivos fallidos de polling */
  reintentosFallidos: number;

  /** True si se alcanzó el límite de reintentos (5 fallos consecutivos) */
  errorComunicacion: boolean;

  /** True si el usuario descartó manualmente este trabajo del panel */
  descartado: boolean;
}

/**
 * Metadata que se pasa al registrar un nuevo trabajo de pago.
 * Contiene la información mínima necesaria para mostrar el trabajo
 * en el panel antes de recibir la primera respuesta del polling.
 */
export interface MetadataRegistroTrabajo {
  /** Número de pago para display en el panel */
  numeroPago: string;

  /** Nombre del proveedor o empresa destino */
  nombreDestino: string;

  /** Total de facturas incluidas en el pago */
  totalFacturas: number;
}

/**
 * Interfaz que representa la respuesta del endpoint
 * GET /api/v1/payments/:id/processing_status del backend.
 * Los nombres de campos están en inglés para coincidir con la API.
 */
export interface RespuestaEstadoProcesamiento {
  /** ID del pago */
  id: number;

  /** Estado de procesamiento actual */
  processing_status: 'pending' | 'processing' | 'completed' | 'completed_with_errors' | 'failed';

  /** Porcentaje de progreso (0-100) */
  progress_percentage: number;

  /** Cantidad de facturas procesadas */
  processed_invoices_count: number;

  /** Total de facturas del pago */
  total_invoices_count: number;

  /** Timestamp ISO de inicio de procesamiento */
  started_at: string | null;

  /** Timestamp ISO de completado */
  completed_at: string | null;

  /** Timestamp ISO de fallo */
  failed_at: string | null;

  /** Tiempo de procesamiento en segundos */
  processing_time: number | null;

  /** Mensaje de error (si aplica) */
  error_message: string | null;
}

/**
 * Estructura almacenada en sessionStorage para persistir los trabajos
 * entre recargas de página. Solo se guarda la información mínima
 * necesaria para reconstruir el panel al volver a la pantalla.
 * Clave en sessionStorage: 'payment_progress_jobs'
 */
export interface RegistroSessionStorage {
  /** ID del pago en el backend */
  id: number;

  /** Número de pago para display al reconectar */
  numeroPago: string;

  /** Nombre del proveedor/empresa para display al reconectar */
  nombreDestino: string;

  /** Total de facturas para display al reconectar */
  totalFacturas: number;

  /** Timestamp ISO de cuando se registró el trabajo */
  registradoEn: string;
}

/**
 * Interfaz pública del servicio de polling de pagos.
 * Define el contrato que el servicio debe cumplir para que
 * el componente del panel pueda suscribirse y controlar los trabajos.
 */
export interface IPaymentPollingService {
  /** Observable con la lista de todos los trabajos (incluye descartados para lógica interna) */
  jobs$: Observable<TrabajoPago[]>;

  /** Registra un nuevo trabajo de pago y comienza el polling */
  registerJob(paymentId: number, metadata: MetadataRegistroTrabajo): void;

  /** Descarta un trabajo completado o fallido (lo marca como descartado y lo remueve de sessionStorage) */
  dismissJob(paymentId: number): void;

  /** Descarta todos los trabajos en estado terminal (completed o failed) */
  dismissAllTerminal(): void;

  /** Inicializa el servicio: lee sessionStorage y reconecta polling para trabajos activos */
  initialize(): void;

  /** Detiene todo el polling activo y limpia suscripciones */
  destroy(): void;
}

// ============================================================================
// CONSTANTES DE CONFIGURACIÓN DEL POLLING
// ============================================================================

/** Intervalo de polling en milisegundos (3 segundos) */
const INTERVALO_POLLING_MS = 3000;

/** Timeout por request HTTP en milisegundos (10 segundos) */
const TIMEOUT_HTTP_MS = 10000;

/** Máximo de fallos consecutivos antes de marcar error de comunicación */
const MAX_FALLOS_CONSECUTIVOS = 5;

/** Máximo de polls simultáneos permitidos */
const MAX_POLLS_SIMULTANEOS = 20;

// ============================================================================
// SERVICIO DE POLLING DE PAGOS
// ============================================================================

/**
 * Servicio Angular que encapsula toda la lógica de polling, gestión de estado
 * y persistencia en sessionStorage para el monitoreo de progreso de pagos.
 *
 * Responsabilidades:
 * - Mantener el estado reactivo de todos los trabajos via BehaviorSubject
 * - Ejecutar polling HTTP cada 3 segundos por trabajo activo (máximo 20 simultáneos)
 * - Persistir IDs en sessionStorage (máximo 50 registros)
 * - Manejar reintentos (máximo 5 consecutivos antes de marcar error de comunicación)
 * - Timeout de 10 segundos por request HTTP
 * - Cancelar suscripciones al destruir
 */
@Injectable()
export class PaymentPollingService implements IPaymentPollingService {

  // Clave utilizada para almacenar los registros de trabajos en sessionStorage
  private readonly STORAGE_KEY = 'payment_progress_jobs';

  // Límite máximo de registros permitidos en sessionStorage
  private readonly MAX_STORAGE_ENTRIES = 50;

  // BehaviorSubject que actúa como store reactivo de todos los trabajos de pago
  private jobsSubject: BehaviorSubject<TrabajoPago[]> = new BehaviorSubject<TrabajoPago[]>([]);

  /** Observable público con la lista de todos los trabajos */
  jobs$: Observable<TrabajoPago[]> = this.jobsSubject.asObservable();

  /**
   * Mapa de suscripciones activas de polling, indexado por ID de pago.
   * Permite cancelar el polling de un trabajo específico sin afectar a los demás.
   */
  private pollingSubscriptions: Map<number, Subscription> = new Map();

  constructor(private http: HttpClient) {
    // El constructor no realiza operaciones; la inicialización se hace en initialize()
  }

  /**
   * Registra un nuevo trabajo de pago con estado inicial 'pending'.
   * Agrega el trabajo al store reactivo, persiste en sessionStorage,
   * e inicia el polling HTTP para este trabajo.
   * @param paymentId - ID del pago en el backend
   * @param metadata - Información mínima para mostrar el trabajo en el panel
   */
  registerJob(paymentId: number, metadata: MetadataRegistroTrabajo): void {
    // Crear el objeto TrabajoPago con valores iniciales
    const nuevoTrabajo: TrabajoPago = {
      id: paymentId,
      numeroPago: metadata.numeroPago,
      nombreDestino: metadata.nombreDestino,
      totalFacturas: metadata.totalFacturas,
      facturasProcessadas: 0,
      porcentajeProgreso: 0,
      estado: 'pending',
      mensajeError: null,
      iniciadoEn: new Date().toISOString(),
      completadoEn: null,
      fallidoEn: null,
      tiempoProcesamiento: null,
      reintentosFallidos: 0,
      errorComunicacion: false,
      descartado: false
    };

    // Agregar el nuevo trabajo al store sin modificar los existentes
    const trabajosActuales = this.jobsSubject.getValue();
    this.jobsSubject.next([...trabajosActuales, nuevoTrabajo]);

    // Persistir en sessionStorage
    this.persistirEnStorage(nuevoTrabajo);

    // Iniciar polling HTTP para este trabajo inmediatamente
    this.iniciarPollingParaTrabajo(paymentId);
  }

  /**
   * Descarta un trabajo marcándolo como descartado en el store
   * y removiéndolo de sessionStorage.
   * @param paymentId - ID del pago a descartar
   */
  dismissJob(paymentId: number): void {
    // Marcar el trabajo como descartado en el store reactivo
    const trabajos = this.jobsSubject.getValue().map(trabajo => {
      if (trabajo.id === paymentId) {
        return { ...trabajo, descartado: true };
      }
      return trabajo;
    });
    this.jobsSubject.next(trabajos);

    // Remover el registro de sessionStorage
    this.removerDeStorage(paymentId);

    // Detener polling si existe para este trabajo
    this.detenerPollingParaTrabajo(paymentId);
  }

  /**
   * Descarta todos los trabajos en estado terminal (completed o failed).
   * Marca cada uno como descartado y los remueve de sessionStorage.
   */
  dismissAllTerminal(): void {
    // Identificar y marcar como descartados todos los trabajos terminales no descartados
    const trabajos = this.jobsSubject.getValue().map(trabajo => {
      if ((trabajo.estado === 'completed' || trabajo.estado === 'completed_with_errors' || trabajo.estado === 'failed') && !trabajo.descartado) {
        return { ...trabajo, descartado: true };
      }
      return trabajo;
    });
    this.jobsSubject.next(trabajos);

    // Remover de sessionStorage todos los trabajos que ahora están descartados
    const idsTerminales = trabajos
      .filter(t => (t.estado === 'completed' || t.estado === 'completed_with_errors' || t.estado === 'failed') && t.descartado)
      .map(t => t.id);

    idsTerminales.forEach(id => this.removerDeStorage(id));
  }

  /**
   * Inicializa el servicio: lee sessionStorage y reconecta polling para trabajos activos.
   * Se llama desde ngOnInit del componente padre (AproveedorComponent).
   * Reconstruye los trabajos desde sessionStorage y arranca el polling
   * para aquellos que no están en estado terminal.
   */
  initialize(): void {
    // Leer registros almacenados en sessionStorage
    const registros = this.leerRegistrosStorage();

    // Si no hay registros, no hay nada que reconstruir
    if (registros.length === 0) {
      return;
    }

    // Reconstruir trabajos a partir de los registros de sessionStorage
    // Se crean con estado 'pending' como placeholder hasta que el polling los actualice
    const trabajosReconstruidos: TrabajoPago[] = registros.map(registro => ({
      id: registro.id,
      numeroPago: registro.numeroPago,
      nombreDestino: registro.nombreDestino,
      totalFacturas: registro.totalFacturas,
      facturasProcessadas: 0,
      porcentajeProgreso: 0,
      estado: 'pending' as EstadoProcesamiento,
      mensajeError: null,
      iniciadoEn: registro.registradoEn,
      completadoEn: null,
      fallidoEn: null,
      tiempoProcesamiento: null,
      reintentosFallidos: 0,
      errorComunicacion: false,
      descartado: false
    }));

    // Emitir los trabajos reconstruidos al BehaviorSubject
    this.jobsSubject.next(trabajosReconstruidos);

    // Iniciar polling para todos los trabajos reconstruidos (todos inician como 'pending')
    // El polling determinará el estado real de cada uno consultando al backend
    trabajosReconstruidos.forEach(trabajo => {
      this.iniciarPollingParaTrabajo(trabajo.id);
    });
  }

  /**
   * Detiene todo el polling activo y limpia todas las suscripciones.
   * Se llama desde ngOnDestroy del componente padre para liberar recursos.
   */
  destroy(): void {
    // Cancelar todas las suscripciones de polling activas
    this.pollingSubscriptions.forEach((subscription, paymentId) => {
      subscription.unsubscribe();
    });

    // Limpiar el mapa de suscripciones
    this.pollingSubscriptions.clear();
  }

  // ============================================================================
  // MÉTODOS PRIVADOS DE POLLING HTTP
  // ============================================================================

  /**
   * Inicia el polling periódico para un trabajo de pago específico.
   * Usa interval(3000) + switchMap para consultar el endpoint cada 3 segundos.
   * Respeta el cap de 20 polls simultáneos máximo.
   * @param paymentId - ID del pago para el cual iniciar el polling
   */
  private iniciarPollingParaTrabajo(paymentId: number): void {
    // Verificar si ya existe un polling activo para este trabajo
    if (this.pollingSubscriptions.has(paymentId)) {
      return; // Ya se está haciendo polling para este trabajo
    }

    // Verificar el cap de 20 polls simultáneos
    if (this.pollingSubscriptions.size >= MAX_POLLS_SIMULTANEOS) {
      return; // Se alcanzó el límite máximo de polls simultáneos
    }

    // Crear suscripción de polling con interval de 3 segundos
    const subscription = interval(INTERVALO_POLLING_MS).pipe(
      // switchMap cancela la petición anterior si no ha terminado antes del siguiente ciclo
      switchMap(() => {
        // Construir la URL del endpoint de estado de procesamiento
        const url = `${environment.URL_SERVICIOS}/payments/${paymentId}/processing_status`;

        // Realizar la petición HTTP GET con timeout de 10 segundos
        return this.http.get<RespuestaEstadoProcesamiento>(url).pipe(
          timeout(TIMEOUT_HTTP_MS)
        );
      })
    ).subscribe(
      // Callback de éxito: procesar la respuesta del backend
      (respuesta: RespuestaEstadoProcesamiento) => {
        this.procesarRespuestaExitosa(paymentId, respuesta);
      },
      // Callback de error: manejar fallo de comunicación
      (error: any) => {
        this.procesarErrorPolling(paymentId, error);
      }
    );

    // Almacenar la suscripción en el mapa para poder cancelarla después
    this.pollingSubscriptions.set(paymentId, subscription);
  }

  /**
   * Procesa una respuesta exitosa del endpoint de estado.
   * Actualiza el estado del trabajo en el store, resetea el contador de reintentos,
   * y detiene el polling si el estado es terminal.
   * @param paymentId - ID del pago que respondió
   * @param respuesta - Respuesta del endpoint de estado de procesamiento
   */
  private procesarRespuestaExitosa(paymentId: number, respuesta: RespuestaEstadoProcesamiento): void {
    // Obtener los trabajos actuales del store
    const trabajos = this.jobsSubject.getValue();

    // Actualizar el trabajo correspondiente con los datos de la respuesta
    const trabajosActualizados = trabajos.map(trabajo => {
      if (trabajo.id === paymentId) {
        return this.mapearRespuestaATrabajo(trabajo, respuesta);
      }
      return trabajo;
    });

    // Emitir el estado actualizado
    this.jobsSubject.next(trabajosActualizados);

    // Si el estado es terminal (completed, completed_with_errors o failed), detener el polling para este trabajo
    if (respuesta.processing_status === 'completed' || respuesta.processing_status === 'completed_with_errors' || respuesta.processing_status === 'failed') {
      this.detenerPollingParaTrabajo(paymentId);
    }
  }

  /**
   * Procesa un error de polling (timeout, error HTTP, error de red).
   * Incrementa el contador de reintentos fallidos y detiene el polling
   * si se alcanza el límite de 5 fallos consecutivos.
   * @param paymentId - ID del pago que falló
   * @param error - Error recibido
   */
  private procesarErrorPolling(paymentId: number, error: any): void {
    // Obtener los trabajos actuales del store
    const trabajos = this.jobsSubject.getValue();

    // Incrementar el contador de reintentos fallidos para el trabajo afectado
    const trabajosActualizados = trabajos.map(trabajo => {
      if (trabajo.id === paymentId) {
        const nuevoContadorFallos = trabajo.reintentosFallidos + 1;

        // Verificar si se alcanzó el límite de 5 fallos consecutivos
        if (nuevoContadorFallos >= MAX_FALLOS_CONSECUTIVOS) {
          // Marcar error de comunicación y detener polling
          return {
            ...trabajo,
            reintentosFallidos: nuevoContadorFallos,
            errorComunicacion: true
          };
        }

        // Incrementar contador sin marcar error de comunicación
        return {
          ...trabajo,
          reintentosFallidos: nuevoContadorFallos
        };
      }
      return trabajo;
    });

    // Emitir el estado actualizado
    this.jobsSubject.next(trabajosActualizados);

    // Verificar si se debe detener el polling por exceso de fallos
    const trabajoAfectado = trabajosActualizados.find(t => t.id === paymentId);
    if (trabajoAfectado && trabajoAfectado.errorComunicacion) {
      this.detenerPollingParaTrabajo(paymentId);
    } else {
      // El polling se detuvo por el error en subscribe, reiniciar para el siguiente ciclo
      // Nota: cuando subscribe recibe un error, la suscripción se completa.
      // Debemos reiniciar el polling para que continúe en el siguiente ciclo.
      this.detenerPollingParaTrabajo(paymentId);
      this.iniciarPollingParaTrabajo(paymentId);
    }
  }

  /**
   * Mapea la respuesta del endpoint de estado a las propiedades del TrabajoPago.
   * Resetea el contador de reintentos a 0 (respuesta exitosa).
   * Trunca el mensaje de error a 200 caracteres si es necesario.
   * @param trabajoActual - Estado actual del trabajo en el store
   * @param respuesta - Respuesta del endpoint de estado
   * @returns Trabajo actualizado con los datos de la respuesta
   */
  private mapearRespuestaATrabajo(trabajoActual: TrabajoPago, respuesta: RespuestaEstadoProcesamiento): TrabajoPago {
    // Conservar el mensaje de error completo del backend (sin truncar)
    const mensajeError: string | null = respuesta.error_message || null;

    // Mapear campos de la respuesta API al modelo interno
    return {
      ...trabajoActual,
      estado: respuesta.processing_status,
      porcentajeProgreso: respuesta.progress_percentage,
      facturasProcessadas: respuesta.processed_invoices_count,
      // Actualizar totalFacturas si el backend reporta un conteo diferente
      totalFacturas: respuesta.total_invoices_count || trabajoActual.totalFacturas,
      // Solo actualizar iniciadoEn si el backend lo reporta y no es null
      iniciadoEn: respuesta.started_at || trabajoActual.iniciadoEn,
      completadoEn: respuesta.completed_at,
      fallidoEn: respuesta.failed_at,
      tiempoProcesamiento: respuesta.processing_time,
      mensajeError: mensajeError,
      // Resetear contador de reintentos a 0 en respuesta exitosa
      reintentosFallidos: 0,
      // Limpiar flag de error de comunicación en respuesta exitosa
      errorComunicacion: false
    };
  }

  /**
   * Detiene el polling para un trabajo específico cancelando su suscripción.
   * @param paymentId - ID del pago cuyo polling se debe detener
   */
  private detenerPollingParaTrabajo(paymentId: number): void {
    const subscription = this.pollingSubscriptions.get(paymentId);
    if (subscription) {
      // Cancelar la suscripción para detener las peticiones HTTP
      subscription.unsubscribe();
      // Remover del mapa de suscripciones activas
      this.pollingSubscriptions.delete(paymentId);
    }
  }

  // ============================================================================
  // MÉTODOS PRIVADOS DE GESTIÓN DE SESSIONSTORAGE
  // ============================================================================

  /**
   * Persiste un nuevo trabajo en sessionStorage respetando el cap de 50 registros.
   * Si se excede el límite, remueve el registro más antiguo por timestamp.
   * Maneja errores de JSON corrupto y QuotaExceededError.
   * @param trabajo - El trabajo de pago a persistir
   */
  private persistirEnStorage(trabajo: TrabajoPago): void {
    // Leer los registros actuales de sessionStorage
    let registros = this.leerRegistrosStorage();

    // Verificar si el cap de 50 se excedería y remover el más antiguo si es necesario
    while (registros.length >= this.MAX_STORAGE_ENTRIES) {
      // Ordenar por fecha de registro y remover el más antiguo
      registros.sort((a, b) => new Date(a.registradoEn).getTime() - new Date(b.registradoEn).getTime());
      registros.shift();
    }

    // Crear el registro para sessionStorage con la información mínima
    const nuevoRegistro: RegistroSessionStorage = {
      id: trabajo.id,
      numeroPago: trabajo.numeroPago,
      nombreDestino: trabajo.nombreDestino,
      totalFacturas: trabajo.totalFacturas,
      registradoEn: trabajo.iniciadoEn
    };

    registros.push(nuevoRegistro);

    // Intentar escribir en sessionStorage con manejo de QuotaExceededError
    this.escribirEnStorage(registros);
  }

  /**
   * Remueve un registro específico de sessionStorage por su ID de pago.
   * @param paymentId - ID del pago a remover del storage
   */
  private removerDeStorage(paymentId: number): void {
    // Leer registros actuales y filtrar el que se quiere remover
    let registros = this.leerRegistrosStorage();
    registros = registros.filter(r => r.id !== paymentId);

    // Escribir los registros actualizados
    this.escribirEnStorage(registros);
  }

  /**
   * Lee y parsea los registros almacenados en sessionStorage.
   * Si el JSON está corrupto o es inválido, limpia el storage y retorna array vacío.
   * @returns Array de registros de sessionStorage
   */
  private leerRegistrosStorage(): RegistroSessionStorage[] {
    try {
      const datos = sessionStorage.getItem(this.STORAGE_KEY);
      if (!datos) {
        return [];
      }
      const parsed = JSON.parse(datos);
      // Validar que el resultado sea un array
      if (!Array.isArray(parsed)) {
        // Datos corruptos: no es un array válido, limpiar storage
        sessionStorage.removeItem(this.STORAGE_KEY);
        return [];
      }
      return parsed;
    } catch (e) {
      // JSON inválido o corrupto: limpiar sessionStorage y retornar vacío
      sessionStorage.removeItem(this.STORAGE_KEY);
      return [];
    }
  }

  /**
   * Escribe los registros en sessionStorage con manejo de QuotaExceededError.
   * Si el storage está lleno, remueve registros antiguos hasta tener espacio.
   * @param registros - Array de registros a escribir
   */
  private escribirEnStorage(registros: RegistroSessionStorage[]): void {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(registros));
    } catch (e) {
      // Manejar QuotaExceededError: remover registros antiguos hasta tener espacio
      if (e && (e as any).name === 'QuotaExceededError') {
        this.liberarEspacioStorage(registros);
      }
    }
  }

  /**
   * Libera espacio en sessionStorage removiendo los registros más antiguos
   * hasta que la escritura sea exitosa o no queden registros.
   * @param registros - Array de registros que se intentan escribir
   */
  private liberarEspacioStorage(registros: RegistroSessionStorage[]): void {
    // Ordenar por fecha de registro (más antiguo primero)
    registros.sort((a, b) => new Date(a.registradoEn).getTime() - new Date(b.registradoEn).getTime());

    // Remover registros antiguos uno por uno hasta que la escritura sea exitosa
    while (registros.length > 0) {
      registros.shift();
      try {
        sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(registros));
        return; // Escritura exitosa, salir del bucle
      } catch (e) {
        // Continuar removiendo si aún no hay espacio
      }
    }

    // Si no quedan registros, limpiar la clave del storage
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {
      // No se puede hacer nada más si sessionStorage no coopera
    }
  }
}
